// Development-only mock data for the WhatsApp campaign pages.
//
// Used only when VUE_APP_ENV === 'development' (see whatsappCampaigns.js). It
// mimics the Knowledge API closely enough to exercise every campaign status and
// the daily broadcast quota without a real WhatsApp inbox. Nothing here touches
// the network, and state lives in memory, so a reload resets it.
//
// Dev switches (set in the browser console, then reload):
//   localStorage.mock_ws_quota = 'exhausted' | 'available' | 'unlimited' | 'real'
//     exhausted (default): the daily limit is spent; Continue answers 409 with resets_at
//     available:           quota left; Continue resumes the campaign
//     unlimited:           a plan without a daily limit (the quota banner is hidden)
//     real:                ask the real backend for the quota (campaigns stay mocked)
//   localStorage.mock_ws_fail = 'refresh,cancel,continue,update'   (any subset)
//     makes those mock calls fail like a server error
//   localStorage.mock_ws_progress = '1'
//     every refresh of the pending campaign moves 5 recipients from pending to sent

export const MOCK_INBOX_ID = 999999;

const MOCK_PREFIX = 'mock-campaign-';
const DAILY_LIMIT = 100;
const TIMEZONE = 'Asia/Kuala_Lumpur';
const DAY_MS = 24 * 60 * 60 * 1000;

// Statuses the backend lets a user cancel or continue (CANCELLABLE statuses).
const CANCELLABLE = ['pending', 'paused_quota', 'awaiting_continuation'];
const CONTINUABLE = ['paused_quota', 'awaiting_continuation'];

export const MOCK_ERROR_MESSAGES = [
  '(#131026) Re-engagement message',
  '(#131047) Template paused',
  '(#131056) Phone number not in allowed list',
  'Recipient opted out',
  'Unknown template variable',
];

const readFlag = key => {
  try {
    return window.localStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
};
const readFlagList = key =>
  readFlag(key)
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

export const isMockCampaignId = id =>
  typeof id === 'string' && id.startsWith(MOCK_PREFIX);

export const mockShouldFail = op => readFlagList('mock_ws_fail').includes(op);

export const mockQuotaMode = () => {
  const mode = readFlag('mock_ws_quota');
  return ['available', 'unlimited', 'real'].includes(mode) ? mode : 'exhausted';
};

const nextMidnightIso = () => {
  const d = new Date();
  d.setHours(24, 0, 0, 0);
  return d.toISOString();
};
const daysAgoIso = days => new Date(Date.now() - days * DAY_MS).toISOString();

const TEMPLATE_COMPONENTS = [
  {
    type: 'header',
    parameters: [
      {
        type: 'image',
        image: {
          link: 'https://images.unsplash.com/photo-1557821552-17105176677c?w=800&q=80',
        },
      },
    ],
  },
  {
    type: 'body',
    parameters: [
      { type: 'text', text: 'John Doe' },
      { type: 'text', text: '50% OFF' },
    ],
  },
];

const WHATSAPP_TEMPLATE_COMPONENTS = [
  { type: 'HEADER', format: 'IMAGE' },
  {
    type: 'BODY',
    text: 'Hi {{1}}! 🎉 Special offer just for you: {{2}} on all items. Limited time only!',
  },
  { type: 'FOOTER', text: 'Reply STOP to unsubscribe' },
  {
    type: 'BUTTONS',
    buttons: [
      { type: 'QUICK_REPLY', text: 'View Offers' },
      { type: 'URL', text: 'Shop Now', url: 'https://example.com/shop' },
    ],
  },
];

// One campaign per status the pages have to handle.
const DEFINITIONS = [
  {
    id: 'mock-campaign-001',
    name: '[DEV] Mock Campaign with Image',
    status: 'pending',
    continuation_mode: 'manual',
    scheduled_time: '2026-04-02T18:49:00',
    created_days_ago: 0,
    status_counts: { pending: 20, sent: 3, failed: 1, cancelled: 1 },
  },
  {
    id: 'mock-campaign-sent',
    name: '[DEV] Mock Campaign: sent',
    status: 'sent',
    continuation_mode: 'manual',
    scheduled_time: '2026-04-01T10:00:00',
    created_days_ago: 9,
    status_counts: { sent: 24, failed: 1 },
  },
  {
    id: 'mock-campaign-failed',
    name: '[DEV] Mock Campaign: failed',
    status: 'failed',
    continuation_mode: 'manual',
    scheduled_time: '2026-04-01T11:00:00',
    created_days_ago: 8,
    status_counts: { failed: 25 },
  },
  {
    id: 'mock-campaign-quota-auto',
    name: '[DEV] Mock Campaign: paused on quota (auto)',
    status: 'paused_quota',
    continuation_mode: 'auto',
    resume_time: '09:00',
    scheduled_time: '2026-04-02T09:00:00',
    created_days_ago: 1,
    status_counts: { sent: 10, pending: 15 },
    quota_used_today: 10,
  },
  {
    id: 'mock-campaign-quota-manual',
    name: '[DEV] Mock Campaign: awaiting continuation (manual)',
    status: 'awaiting_continuation',
    continuation_mode: 'manual',
    scheduled_time: '2026-04-02T09:30:00',
    created_days_ago: 1,
    status_counts: { sent: 10, pending: 15 },
    quota_used_today: 10,
  },
];

let overrides = {};
let progress = {};

const sumCounts = counts =>
  Object.values(counts || {}).reduce((s, n) => s + (Number(n) || 0), 0);

const countsFor = def => {
  const counts = { ...def.status_counts };
  const moved = progress[def.id] || 0;
  if (moved) {
    const m = Math.min(moved, counts.pending || 0);
    counts.pending = (counts.pending || 0) - m;
    counts.sent = (counts.sent || 0) + m;
  }
  return counts;
};

const buildCampaign = def => {
  const override = overrides[def.id] || {};
  const counts = override.status_counts || countsFor(def);
  const status = override.status || def.status;
  const parked = CONTINUABLE.includes(status);
  const campaign = {
    id: def.id,
    name: def.name,
    template_name: 'kumho_mock_clearance_en',
    language_code: 'en',
    status,
    continuation_mode: def.continuation_mode,
    resume_time: def.resume_time,
    scheduled_time: def.scheduled_time,
    created_at: daysAgoIso(def.created_days_ago),
    updated_at: new Date().toISOString(),
    num_of_recipients: sumCounts(counts),
    status_counts: counts,
    inbox_id: MOCK_INBOX_ID,
    inbox_name: 'Mock WhatsApp Inbox',
    channel_type: 'Channel::Whatsapp',
    account_id: 148,
    template_components: TEMPLATE_COMPONENTS,
    whatsapp_template_components: WHATSAPP_TEMPLATE_COMPONENTS,
    ...(def.quota_used_today != null
      ? { quota_used_today: def.quota_used_today }
      : {}),
    ...(parked
      ? {
          quota_hold: {
            remaining: counts.pending || 0,
            resets_at: nextMidnightIso(),
            timezone: TIMEZONE,
            resume_time: def.resume_time,
          },
        }
      : {}),
    ...override,
  };
  return campaign;
};

const definitionFor = id => DEFINITIONS.find(d => d.id === id);

export const getMockCampaign = id => {
  const def = definitionFor(id);
  return def ? buildCampaign(def) : null;
};

// `filters` uses the same names as fetchTemplateCampaigns' arguments.
export const getMockCampaigns = ({
  status = '',
  inbox_id: inboxId = '',
  name = '',
  template_name: templateName = '',
  num_recipients_min: min = '',
  num_recipients_max: max = '',
  created_at: createdAt = '',
  q = '',
} = {}) =>
  DEFINITIONS.map(buildCampaign).filter(c => {
    if (status && c.status !== status) return false;
    if (inboxId && String(c.inbox_id) !== String(inboxId)) return false;
    if (name && !c.name.toLowerCase().includes(name.toLowerCase())) return false;
    if (
      templateName &&
      !c.template_name.toLowerCase().includes(templateName.toLowerCase())
    )
      return false;
    if (min !== '' && c.num_of_recipients < parseInt(min, 10)) return false;
    if (max !== '' && c.num_of_recipients > parseInt(max, 10)) return false;
    if (createdAt) {
      const [operator, dateStr] = createdAt.split(':');
      if (dateStr) {
        const filterDate = new Date(dateStr);
        const created = new Date(c.created_at);
        if (operator === 'after' && created <= filterDate) return false;
        if (operator === 'before' && created >= filterDate) return false;
        if (
          operator === 'on' &&
          created.toISOString().split('T')[0] !==
            filterDate.toISOString().split('T')[0]
        )
          return false;
      }
    }
    if (q) {
      const haystack = [c.name, c.template_name, c.inbox_name, c.status]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q.toLowerCase())) return false;
    }
    return true;
  });

const FIRST_NAMES = ['Aisha', 'Ben', 'Chloe', 'Daniel', 'Elaine', 'Farid', 'Grace', 'Hui Min'];

const buildRecipients = def => {
  const counts = countsFor(def);
  const order = ['sent', 'failed', 'cancelled', 'pending'];
  const list = [];
  let n = 0;
  order.forEach(status => {
    for (let i = 0; i < (counts[status] || 0); i += 1) {
      n += 1;
      const first = FIRST_NAMES[n % FIRST_NAMES.length];
      list.push({
        id: `${def.id}-r${n}`,
        status,
        contact_info: {
          name: `${first} ${n}`,
          phone_number: `+6012${String(3000000 + n)}`,
          email: `${first.toLowerCase().replace(' ', '')}${n}@example.com`,
        },
        sent_at:
          status === 'sent' ? new Date(Date.now() - n * 60000).toISOString() : null,
        error_message:
          status === 'failed'
            ? MOCK_ERROR_MESSAGES[n % MOCK_ERROR_MESSAGES.length]
            : null,
      });
    }
  });
  return list;
};

export const getMockRecipients = (
  id,
  { status = '', error_message: errorMessage = '', page = 1, page_size: pageSize = 100 } = {}
) => {
  const def = definitionFor(id);
  if (!def) return { results: [], total: 0, page, page_size: pageSize };
  let rows = buildRecipients(def);
  if (status) rows = rows.filter(r => r.status === status);
  if (errorMessage) rows = rows.filter(r => r.error_message === errorMessage);
  const start = (page - 1) * pageSize;
  return {
    results: rows.slice(start, start + pageSize),
    total: rows.length,
    page,
    page_size: pageSize,
  };
};

// Refreshing the pending campaign can show recipients being sent.
export const touchMockCampaign = id => {
  if (readFlag('mock_ws_progress') === '1' && id === 'mock-campaign-001') {
    progress[id] = (progress[id] || 0) + 5;
  }
};

export const mockCancel = id => {
  const campaign = getMockCampaign(id);
  if (!campaign || !CANCELLABLE.includes(campaign.status)) {
    throw new Error('Error cancelling template campaign');
  }
  overrides[id] = { ...overrides[id], status: 'cancelled' };
  return { success: true };
};

export const mockUpdate = (id, payload) => {
  overrides[id] = { ...overrides[id], ...payload };
  return { success: true };
};

export const mockUpdateRecipients = (id, payload) => {
  const count = (payload.contact_ids || []).length;
  overrides[id] = {
    ...overrides[id],
    status_counts: { pending: count },
  };
  return { success: true };
};

export const getMockQuota = () => {
  const mode = mockQuotaMode();
  if (mode === 'real') return null;
  const campaigns = DEFINITIONS.filter(d => d.quota_used_today != null).map(d => ({
    id: d.id,
    name: d.name,
    used: d.quota_used_today,
  }));
  const base = {
    usage_date: new Date().toISOString().split('T')[0],
    resets_at: nextMidnightIso(),
    timezone: TIMEZONE,
    campaigns,
  };
  if (mode === 'unlimited') {
    return { ...base, limit: null, used: 0, remaining: null, unlimited: true };
  }
  const used = mode === 'available' ? 40 : DAILY_LIMIT;
  return {
    ...base,
    limit: DAILY_LIMIT,
    used,
    remaining: DAILY_LIMIT - used,
    unlimited: false,
  };
};

// Mirrors the real 409: the error carries resetsAt like the store's raw-fetch path.
export const mockContinue = id => {
  const campaign = getMockCampaign(id);
  if (!campaign || !CONTINUABLE.includes(campaign.status)) {
    throw new Error('Failed to continue template campaign');
  }
  const quota = getMockQuota();
  if (quota && !quota.unlimited && quota.remaining === 0) {
    const err = new Error('No broadcast quota left today');
    err.resetsAt = quota.resets_at;
    throw err;
  }
  overrides[id] = { ...overrides[id], status: 'pending', quota_hold: null };
  return { success: true };
};

export const resetMockCampaigns = () => {
  overrides = {};
  progress = {};
};
